import type { Cents } from "../types/money";
import { DEFAULT_BEWERKINGSTARIEVEN, type BewerkingsTarieven } from "./bewerkingstarieven";
import type { GlassVariant, OpTier } from "../types/product";
import type { ProcessingRequest } from "../types/config";
import type { ProcessingLine } from "../types/pricing";
import { PricingError } from "./errors";
import { RANDUITSPARING_GEHARD_GELAAGD, boringGehardGelaagdCents, type GrindFamily } from "./grinding";

/**
 * Binnenhoeken van een uitsparing kunnen niet haaks gemaakt worden — er blijft altijd een
 * afronding staan. De minimale straal hangt af van de afwerking én van het glas:
 *
 *   · rand ongeslepen ("vlak mat")  → minimaal R7  (Ø14)
 *   · rand geslepen  ("vlak poli")  → minimaal R13 (Ø26)
 *   · gehard glas, ongeslepen       → de glasdikte is de minimale straal (10 mm glas → R10)
 *
 * Bij gehard glas houden we de algemene ondergrens van 7 mm aan als de plaat dunner is dan dat;
 * de regel uit het prijzenboek maakt de hoek voor dík gehard glas juist ruimer, niet krapper.
 */
export const BINNENHOEK_MIN_MAT_MM = 7;
export const BINNENHOEK_MIN_POLI_MM = 13;

export function binnenhoekRadiusMm(opts: {
  thicknessMm: number;
  /** Wordt de rand van de uitsparing PP-geslepen? */
  geslepen: boolean;
  /** Geharde soort (ESG) — daar bepaalt de glasdikte de minimale straal. */
  gehard: boolean;
}): number {
  const { thicknessMm, geslepen, gehard } = opts;
  if (geslepen) return BINNENHOEK_MIN_POLI_MM;
  const basis = BINNENHOEK_MIN_MAT_MM;
  return gehard && Number.isFinite(thicknessMm) ? Math.max(basis, Math.round(thicknessMm)) : basis;
}

/**
 * Installatieglas (deurglas): de m²-prijs van Timmers bevat PP-slijpen én maximaal vijf
 * uitsparingen voor glasbeslag. Pas wat daarbóven zit wordt los gerekend.
 *
 * Deze aftrek moet aan beide kanten identiek gebeuren — de configurator toont 'm en `createOrder`
 * herprijst ermee. Vandaar één functie hier in core in plaats van een sommetje in de UI.
 */
export const INSTALLATIEGLAS_GRATIS_SPARINGEN = 5;

/**
 * Representatieve maat (mm) van een scharnieruitsparing. De échte vorm verschilt per fabrikaat en
 * staat in geen enkele artikelomschrijving; de douche-module rekent daarom met dezelfde 50 mm
 * (HINGE_CUTOUT_MM in articles/shower.ts). Eén maat voor beide, zodat een scharnier in de deur
 * hetzelfde kost als een scharnier in een douchedeur.
 */
export const SCHARNIER_UITSPARING_MM = 50;

/** Slug(s) waarvoor die vijf gratis sparingen gelden. */
const GRATIS_SPARING_SLUGS = new Set(["installatieglas"]);

/**
 * Installatieglas (deurglas) wordt door Timmers altijd rondom PP-geslepen geleverd; dat slijpwerk
 * zit in de m²-prijs (GLASS_DOORS). Het is dus geen keuze en er komt geen tarief bij — reken je het
 * er toch bij, dan betaalt de klant twee keer voor dezelfde bewerking.
 */
export function heeftInbegrepenPP(productSlug: string | undefined): boolean {
  return GRATIS_SPARING_SLUGS.has((productSlug ?? "").trim());
}

export function heeftGratisSparingen(productSlug: string | undefined): boolean {
  return GRATIS_SPARING_SLUGS.has((productSlug ?? "").trim());
}

/**
 * Trekt de inbegrepen sparingen van de aanvraag af. De regels blijven staan (productie moet ze
 * maken), alleen het aantal dat betaald wordt zakt — is alles inbegrepen, dan valt de regel weg.
 *
 * Zowel uitsparingen als boringen tellen mee: een deur krijgt scharnieruitsparingen én een gat
 * voor de knop, en dat hoort allemaal binnen de vijf die de m²-prijs al dekt. Uitsparingen gaan
 * voor, want die zijn duurder — zo levert de vrijstelling de klant het meeste op. Ronde hoeken
 * vallen er buiten; dat is randwerk, geen sparing.
 */
export function metGratisSparingen(
  requests: ProcessingRequest[] | undefined,
  productSlug: string | undefined,
): ProcessingRequest[] | undefined {
  if (!requests?.length || !heeftGratisSparingen(productSlug)) return requests;
  let vrij = INSTALLATIEGLAS_GRATIS_SPARINGEN;
  const rest = new Map<number, number>();
  // Twee rondes (uitsparingen eerst), maar de volgorde van de regels blijft zoals 'ie binnenkwam.
  for (const soort of ["uitsparing", "boring"] as const) {
    requests.forEach((r, i) => {
      if (r.kind !== soort) return;
      const gratis = Math.min(Math.max(vrij, 0), r.count);
      vrij -= gratis;
      rest.set(i, r.count - gratis);
    });
  }
  const uit: ProcessingRequest[] = [];
  requests.forEach((r, i) => {
    const over = rest.get(i);
    if (over == null) { uit.push(r); return; }
    if (over > 0) uit.push({ ...r, count: over });
  });
  return uit;
}

/** Default radius used when a corner is rounded without an explicit size. */
export const DEFAULT_CORNER_RADIUS_MM = 100;

const LABELS: Record<ProcessingRequest["kind"], string> = {
  boring: "Boring",
  uitsparing: "Uitsparing",
  hoek: "Ronde hoek",
};

/** Price for `value` in ascending tiers; null when above the largest tier. */
function tierCost(tiers: OpTier[] | undefined, value: number): Cents | null {
  if (!tiers?.length) return null;
  const tier = tiers.find((t) => value <= t.maxMm);
  return tier ? tier.cost : null;
}

/**
 * Boorprijs: €0,41 per mm diameter, met een minimum van €4,70 (inkoop).
 * Bv. Ø10 → max(470, 410) = €4,70; Ø20 → €8,20. Null als deze ruit niet geboord
 * kan worden (geen boring-staffel in de feed = niet boorbaar).
 */
export const BORING_PER_MM_CENTS = DEFAULT_BEWERKINGSTARIEVEN.boringPerMmCents;
export const BORING_MIN_CENTS = DEFAULT_BEWERKINGSTARIEVEN.boringMinCents;

export function boringCost(
  variant: GlassVariant,
  diameterMm: number,
  family: GrindFamily = "enkel",
  tarieven: BewerkingsTarieven = DEFAULT_BEWERKINGSTARIEVEN,
): Cents | null {
  if (!(diameterMm > 0)) return null;
  // Gehard-gelaagd heeft een eigen stukprijs-tabel (prijzenboek pag. 20): per dikste ruit en
  // gatdiameter, niet de €/mm-formule van enkel glas. Staat de combinatie er niet in (bv. 2×6 mm
  // met een gat t/m 25 mm), dan is 'ie niet online te prijzen → null.
  if (family === "gehard-gelaagd") return boringGehardGelaagdCents(variant.thicknessLabel ?? variant.thicknessMm, diameterMm);
  if (!variant.ops?.boring?.length) return null;
  return Math.max(tarieven.boringMinCents, Math.round(tarieven.boringPerMmCents * diameterMm)) as Cents;
}

/**
 * Uitsparing (hapje / stopcontact), geprijsd op de LANGSTE zijde.
 *
 * Twee staffels: standaard blijft de rand onbewerkt → "vlak mat". Wordt de rand van díé sparing
 * PP-geslepen (de configurator zet dan `finished`), dan geldt "vlak poli". Er zit geen keuze
 * tussenin: het volgt automatisch de rand die je aanklikt.
 *
 * De tarieven komen uit de Timmers-sync (prijstabel × de dealerkorting op INSET) en lopen t/m de
 * open band boven 1500 mm. Eén cel in de gedrukte tabel wijkt af — "vlak poli < 500 mm" bij
 * 4-6 mm staat daar op €27,14, terwijl vlak MAT op diezelfde regel €47,63 is; de API geeft
 * €57,14 en dat past bij het patroon (poli ≈ mat × 1,2). Dat is dus een typefout in de tabel,
 * niet in de sync — niet "corrigeren" naar €27,14.
 */
export function uitsparingCost(
  variant: GlassVariant,
  longestSideMm: number,
  finished?: boolean,
  family: GrindFamily = "enkel",
): Cents | null {
  // Gehard-gelaagd: één vaste stukprijs naar de TOTALE pakketdikte (€42,41 t/m 10 mm, €60,62 t/m
  // 26 mm), altijd afgescherpt. Geen maat-staffel en geen mat/poli-keuze — zo staat het in het
  // werkblad voor deze soort.
  if (family === "gehard-gelaagd") {
    const totaalMm = Math.round(variant.thicknessMm);
    return RANDUITSPARING_GEHARD_GELAAGD.find((b) => totaalMm <= b.totMm)?.cents ?? null;
  }
  const tiers = finished ? (variant.ops?.uitsparingPoli ?? variant.ops?.uitsparing) : variant.ops?.uitsparing;
  return tierCost(tiers, longestSideMm);
}

/**
 * Hoekafwerking, geprijsd op de maat (radius bij rond, afsnijding bij schuin).
 *
 * "schuin-poli" (Timmers "RP") en "schuin-brut" zijn dezelfde schuine hoek met een andere
 * randafwerking; welke van de twee geldt volgt uit de rand van de plaat, niet uit een losse
 * klantkeuze. De "gebroken" hoek is een ander product met één vaste prijs, dus daar telt de maat
 * niet mee — vandaar de open staffel.
 */
export function hoekCost(
  variant: GlassVariant,
  sizeMm: number,
  hoekType: "rond" | "schuin-poli" | "schuin-brut" | "gebroken" = "rond",
): Cents | null {
  const tiers =
    hoekType === "schuin-poli" ? variant.ops?.schuineHoekPoli
    : hoekType === "schuin-brut" ? variant.ops?.schuineHoekBrut
    : hoekType === "gebroken" ? variant.ops?.gebrokenHoek
    : variant.ops?.hoek;
  return tierCost(tiers, sizeMm);
}

function lookup(
  variant: GlassVariant,
  req: ProcessingRequest,
  family: GrindFamily,
  tarieven: BewerkingsTarieven,
): Cents | null {
  switch (req.kind) {
    case "boring":
      return boringCost(variant, req.sizeMm, family, tarieven);
    case "uitsparing":
      return uitsparingCost(variant, req.sizeMm, req.finished, family);
    case "hoek":
      return hoekCost(variant, req.sizeMm, req.hoekType);
  }
}

/**
 * Resolves requested operations into costed lines (cost = unit × count, raw).
 * @throws {PricingError} when a request is malformed or unavailable for the variant.
 */
export function resolveProcessingLines(
  requests: ProcessingRequest[] | undefined,
  variant: GlassVariant,
  family: GrindFamily = "enkel",
  tarieven: BewerkingsTarieven = DEFAULT_BEWERKINGSTARIEVEN,
): ProcessingLine[] {
  if (!requests?.length) return [];

  const lines: ProcessingLine[] = [];
  const errors: string[] = [];

  for (const req of requests) {
    const valid =
      Number.isFinite(req.sizeMm) &&
      req.sizeMm > 0 &&
      Number.isInteger(req.count) &&
      req.count >= 1;
    if (!valid) {
      errors.push(`Ongeldige bewerking opgegeven (${LABELS[req.kind]}).`);
      continue;
    }
    const unit = lookup(variant, req, family, tarieven);
    if (unit == null) {
      errors.push(
        `${LABELS[req.kind]} van ${req.sizeMm} mm is niet mogelijk voor ${variant.name}.`,
      );
      continue;
    }
    const base =
      req.kind === "hoek" && req.hoekType === "schuin-poli" ? "Schuine hoek (geslepen)"
      : req.kind === "hoek" && req.hoekType === "schuin-brut" ? "Schuine hoek (onbewerkt)"
      : req.kind === "hoek" && req.hoekType === "gebroken" ? "Gebroken hoek"
      : LABELS[req.kind];
    // Bij gehard-gelaagd is de sparing altijd afgescherpt (één prijs), dus daar geen (geslepen)-
    // achtervoegsel dat een keuze suggereert die er niet is.
    const suffix =
      req.kind === "uitsparing" && family === "gehard-gelaagd" ? " (afgescherpt)"
      : req.kind === "uitsparing" && req.finished ? " (geslepen)"
      : "";
    const label = `${base} ${req.sizeMm} mm${suffix}`;
    lines.push({
      name: `${label} × ${req.count}`,
      label,
      count: req.count,
      unitCost: unit,
      cost: unit * req.count,
    });
  }

  if (errors.length > 0) throw new PricingError(errors);
  return lines;
}
